/**
 * Create Character Wizard - Redesigned
 * Step-by-step character creation with new structure
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

function CreateCharacter() {
  const { user, language, updateUser } = React.useContext(AuthContext);
  const navigate = useNavigate();
  
  const [step, setStep] = useState(0); // Step 0: Gender + Realism
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [realismLevel, setRealismLevel] = useState(null); // "ultra_realistic" or "realistic"
  const [characterGender, setCharacterGender] = useState('female'); // "female" | "male"
  
  // Step 1: Appearance - 8 dropdowns
  const [appearance, setAppearance] = useState({
    ethnicity: '',
    skin_tone: '',
    eye_color: '',
    hair_color: '',
    hair_style: '',
    body_type: '',
    breast_size: '',
    butt_size: ''
  });
  
  // Step 2: Character Info
  const [characterInfo, setCharacterInfo] = useState({
    name: '',
    age: '',
    greeting: '',
    scenario: '',
    public_description: ''
  });
  
  // Step 3: Generation
  const [generationPrompt, setGenerationPrompt] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [tags, setTags] = useState([]);
  const [previewSeed, setPreviewSeed] = useState(null);
  const [randomInfoLoading, setRandomInfoLoading] = useState(false);
  const [aiPromptLoading, setAiPromptLoading] = useState(false);

  const translations = {
    en: {
      title: 'Create Character',
      step0: 'Style',
      step1: 'Appearance',
      step2: 'Character Info',
      step3: 'Generation',
      next: 'Next',
      back: 'Back',
      generate: 'Generate my girlfriend',
      generateMale: 'Generate my character',
      generating: 'Generating...',
      create: 'Create Character',
      error: 'Error',
      chooseGender: 'Character gender',
      genderFemale: 'Female',
      genderMale: 'Male',
      chooseRealism: 'Choose Realism Style',
      random: 'Random',
      randomAppearance: 'Random',
      randomCharacterInfo: 'Random',
      generatingInfo: 'Generating...',
      // Appearance fields
      ethnicity: 'Ethnicity',
      skinTone: 'Skin Tone',
      eyeColor: 'Eye Color',
      hairColor: 'Hair Color',
      hairStyle: 'Hair Style',
      bodyType: 'Body Type',
      breastSize: 'Breast Size',
      breastSizeMale: 'Build',
      buttSize: 'Butt Size',
      // Character Info fields
      characterName: 'Character Name',
      age: 'Age',
      greeting: 'Greeting',
      scenario: 'Scenario',
      publicDescription: 'Public Description',
      // Generation
      individualCharacteristics: 'Individual Characteristics',
      aiGeneration: 'AI Generation',
      generationHint: 'Describe specific appearance details that will be used for photo generation',
      tags: 'Tags',
      tagsHint: 'Add up to 5 tags describing your character (separated by space or comma)',
      moderationNote: 'Before becoming public, your character must pass moderation.',
      cost: 'Cost: 39 tokens',
      select: 'Select...',
      namePlaceholder: '3-24 characters',
      agePlaceholder: '18-60',
      greetingPlaceholder: 'First message that the character will send...',
      scenarioPlaceholder: 'Character background and behavior for AI...',
      descriptionPlaceholder: 'This will appear in the ABOUT section...',
      generationPlaceholder: 'Describe unique appearance features for photo generation...',
      insufficientTokens: 'Insufficient tokens. Need 39 tokens for character creation.',
      nameTooShort: 'Name must be at least 3 characters',
      nameTooLong: 'Name must be no more than 24 characters',
      ageInvalid: 'Age must be between 18 and 60',
      requiredField: 'This field is required'
    },
    ru: {
      title: 'Создать персонажа',
      step0: 'Стиль',
      step1: 'Внешность',
      step2: 'Информация о персонаже',
      step3: 'Генерация',
      next: 'Далее',
      back: 'Назад',
      generate: 'Сгенерировать мою подругу',
      generateMale: 'Сгенерировать персонажа',
      generating: 'Генерация...',
      create: 'Создать персонажа',
      error: 'Ошибка',
      chooseGender: 'Пол персонажа',
      genderFemale: 'Женский',
      genderMale: 'Мужской',
      chooseRealism: 'Выберите стиль реалистичности',
      random: 'Случайно',
      randomAppearance: 'Случайно',
      randomCharacterInfo: 'Случайно',
      generatingInfo: 'Генерация...',
      // Appearance fields
      ethnicity: 'Этническая принадлежность',
      skinTone: 'Оттенок кожи',
      eyeColor: 'Цвет глаз',
      hairColor: 'Цвет волос',
      hairStyle: 'Прическа',
      bodyType: 'Тип тела',
      breastSize: 'Размер груди',
      breastSizeMale: 'Телосложение',
      buttSize: 'Размер ягодиц',
      // Character Info fields
      characterName: 'Имя персонажа',
      age: 'Возраст',
      greeting: 'Приветствие',
      scenario: 'Сценарий',
      publicDescription: 'Публичное описание',
      // Generation
      individualCharacteristics: 'Особенности внешнего вида',
      aiGeneration: 'AI генерация',
      generationHint: 'Опишите уникальные особенности внешности для генерации фото',
      tags: 'Теги',
      tagsHint: 'Добавьте до 5 тегов, описывающих персонажа (через пробел или запятую)',
      moderationNote: 'Перед тем как стать публичной, ваша модель должна пройти модерацию.',
      cost: 'Стоимость: 19 токенов',
      select: 'Выбрать...',
      namePlaceholder: '3-24 символа',
      agePlaceholder: '18-60',
      greetingPlaceholder: 'Первое сообщение, которое отправит персонаж...',
      scenarioPlaceholder: 'Предыстория и поведение персонажа для AI...',
      descriptionPlaceholder: 'Это будет отображаться в разделе ABOUT...',
      generationPlaceholder: 'Опишите уникальные особенности внешности для генерации фото...',
      insufficientTokens: 'Недостаточно токенов. Нужно 39 токенов для создания персонажа.',
      nameTooShort: 'Имя должно быть не менее 3 символов',
      nameTooLong: 'Имя должно быть не более 24 символов',
      ageInvalid: 'Возраст должен быть от 18 до 60 лет',
      requiredField: 'Это поле обязательно'
    }
  };

  const t = translations[language] || translations.en;

  // Options from screenshots
  const ethnicityOptions = ['Asian', 'Black', 'White', 'Latina', 'Arab', 'Indian', 'Elf', 'Alien', 'Demon'];
  const skinToneOptions = ['Fair', 'Light', 'Medium Light', 'Medium', 'Medium Dark', 'Dark'];
  const eyeColorOptions = ['Black', 'Brown', 'Red', 'Yellow', 'Green', 'Blue', 'Purple', 'Pink', 'White', 'Grey'];
  const hairColorOptions = ['Black', 'Brown', 'Red', 'Blonde', 'Green', 'Blue', 'Purple', 'Pink', 'White', 'Grey'];
  const hairStyleOptions = ['Braided', 'Long', 'Bangs', 'Ponytail', 'Short', 'Bun', 'Buns', 'Wavy', 'Pixie', 'Custom'];
  const bodyTypeOptions = ['Slim', 'Athletic', 'Voluptuous', 'Curvy', 'Muscular'];
  const breastSizeOptions = ['Flat', 'Small', 'Medium', 'Large', 'XL'];
  const buttSizeOptions = ['Small', 'Skinny', 'Athletic', 'Medium', 'Large'];

  const handleTagsChange = (value) => {
    setTagsInput(value);
    const rawTags = value
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    const unique = Array.from(new Set(rawTags));
    setTags(unique.slice(0, 5));
  };

  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

  const handleRandomAppearance = () => {
    const toOpt = (arr) => pick(arr).toLowerCase();
    const toOptSkin = (arr) => pick(arr).toLowerCase().replace(' ', '_');
    setAppearance({
      ethnicity: toOpt(ethnicityOptions),
      skin_tone: toOptSkin(skinToneOptions),
      eye_color: toOpt(eyeColorOptions),
      hair_color: toOpt(hairColorOptions),
      hair_style: toOpt(hairStyleOptions),
      body_type: toOpt(bodyTypeOptions),
      breast_size: toOpt(breastSizeOptions),
      butt_size: toOpt(buttSizeOptions)
    });
  };

  const handleRandomCharacterInfo = async () => {
    setRandomInfoLoading(true);
    setError('');
    try {
      const { data } = await axios.post('/web/characters/random_character_info', {
        gender: characterGender,
        language: language === 'ru' ? 'ru' : 'en'
      });
      setCharacterInfo({
        name: data.name || '',
        age: data.age || '25',
        greeting: data.greeting || '',
        scenario: data.scenario || '',
        public_description: data.public_description || ''
      });
      if (Array.isArray(data.tags) && data.tags.length > 0) {
        setTags(data.tags.slice(0, 5));
        setTagsInput(data.tags.join(', '));
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setRandomInfoLoading(false);
    }
  };

  const handleAiGenerationPrompt = async () => {
    setAiPromptLoading(true);
    setError('');
    try {
      const { data } = await axios.post('/web/characters/ai/generate_individual_prompt', {
        appearance: { ...appearance, gender: characterGender },
        character_info: characterInfo,
        language: language === 'ru' ? 'ru' : 'en'
      });
      if (data.generation_prompt) {
        setGenerationPrompt(data.generation_prompt);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setAiPromptLoading(false);
    }
  };

  const handleNext = () => {
    setError('');
    
    // Validation for step 0 (realism level selection)
    if (step === 0) {
      if (!realismLevel) {
        setError(language === 'en' ? 'Please select a realism style' : 'Пожалуйста, выберите стиль реалистичности');
        return;
      }
    }
    
    // Validation for step 1
    if (step === 1) {
      const required = ['ethnicity', 'skin_tone', 'eye_color', 'hair_color', 'hair_style', 'body_type', 'breast_size', 'butt_size'];
      const missing = required.filter(field => !appearance[field]);
      if (missing.length > 0) {
        setError(language === 'en' ? 'Please fill all appearance fields' : 'Заполните все поля внешности');
        return;
      }
    }
    
    // Validation for step 2
    if (step === 2) {
      if (!characterInfo.name || characterInfo.name.length < 3) {
        setError(t.nameTooShort);
        return;
      }
      if (characterInfo.name.length > 24) {
        setError(t.nameTooLong);
        return;
      }
      const age = parseInt(characterInfo.age);
      if (!characterInfo.age || isNaN(age) || age < 18 || age > 60) {
        setError(t.ageInvalid);
        return;
      }
      if (!characterInfo.greeting.trim()) {
        setError(t.requiredField + ': ' + t.greeting);
        return;
      }
      if (!characterInfo.scenario.trim()) {
        setError(t.requiredField + ': ' + t.scenario);
        return;
      }
      if (!characterInfo.public_description.trim()) {
        setError(t.requiredField + ': ' + t.publicDescription);
        return;
      }
    }
    
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setError('');
    }
  };

  const handleGenerate = async () => {
    if (!user || user.token_balance < 19) {
      setError(language === 'en' ? 'Insufficient tokens. Need 19 tokens for photo generation.' : 'Недостаточно токенов. Нужно 19 токенов для генерации фото.');
      return;
    }

    if (!generationPrompt.trim()) {
      setError(t.requiredField + ': ' + t.individualCharacteristics);
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await axios.post('/web/characters/generate_preview', {
        appearance: { ...appearance, gender: characterGender },
        generation_prompt: generationPrompt,
        age: parseInt(characterInfo.age) || null,
        realism_level: realismLevel || 'ultra_realistic'
      });

      setAvatarUrl(response.data.avatar_url);
      setPreviewSeed(response.data.seed);
      
      // Refresh user balance after token deduction
      if (updateUser && user) {
        const userResponse = await axios.get('/auth/me');
        updateUser(userResponse.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!user || user.token_balance < 39) {
      setError(t.insufficientTokens);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/web/characters/create', {
        appearance: { ...appearance, gender: characterGender },
        character_info: {
          ...characterInfo,
          tags
        },
        generation_prompt: generationPrompt,
        avatar_url: avatarUrl,
        seed: previewSeed,
        realism_level: realismLevel || 'ultra_realistic'
      });

      // Refresh user balance after token deduction
      if (updateUser && user) {
        const userResponse = await axios.get('/auth/me');
        updateUser(userResponse.data);
      }

      navigate(`/web/chat/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-white text-center py-12">
        {language === 'en' ? 'Please login to create characters' : 'Пожалуйста, войдите для создания персонажей'}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4">
      <h1 className="text-3xl font-bold text-white mb-6">{t.title}</h1>

      {/* Wrapper to keep progress bar and content the same fixed width on all steps - 864px exactly */}
      <div className="mx-auto" style={{ width: '864px', maxWidth: '864px' }}>
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[0, 1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= s ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {s + 1}
                  </div>
                  <span className="text-xs mt-2 text-gray-400">
                    {t[`step${s}`]}
                  </span>
                </div>
                {s < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      step > s ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900 text-red-200 rounded">
            {error}
          </div>
        )}

        {/* Step 0: Gender + Realism Level Selection */}
        {step === 0 && (
        <div
          className="bg-gray-800 rounded-lg p-6 min-h-[600px]"
          style={{ width: '864px', maxWidth: '864px' }}
        >
          <h2 className="text-2xl font-bold text-white mb-2 text-center">{t.chooseGender}</h2>
          <div className="flex justify-center gap-4 mb-6">
            <button
              type="button"
              onClick={() => setCharacterGender('female')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                characterGender === 'female'
                  ? 'bg-pink-600 text-white ring-2 ring-pink-400'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {t.genderFemale}
            </button>
            <button
              type="button"
              onClick={() => setCharacterGender('male')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                characterGender === 'male'
                  ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {t.genderMale}
            </button>
          </div>

          <h2 className="text-xl font-bold text-white mb-2 text-center">{t.chooseRealism}</h2>
          <p className="text-gray-400 text-center mb-6">
            {language === 'en'
              ? 'Select the style for your character generation'
              : 'Выберите стиль для генерации вашего персонажа'}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ultra Realistic Option */}
            <div
              onClick={() => setRealismLevel('ultra_realistic')}
              className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                realismLevel === 'ultra_realistic' 
                  ? 'border-blue-500 ring-4 ring-blue-500/50' 
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="aspect-[9/16] bg-gray-900 relative">
                <img 
                  src="https://hotbump.online/static/ultra_realistic_example.jpg" 
                  alt="Ultra Realistic"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-500">Ultra Realistic<br/>Example</div>';
                  }}
                />
              </div>
              <div className="p-4 bg-gray-900">
                <h3 className="text-xl font-bold text-white mb-2 text-center">
                  {language === 'en' ? 'Ultra Realistic' : 'Супер реалистично'}
                </h3>
                <p className="text-sm text-gray-400 text-center">
                  {language === 'en' 
                    ? 'Photo-realistic, highly detailed images' 
                    : 'Фотореалистичные, детализированные изображения'}
                </p>
              </div>
            </div>

            {/* Realistic Option */}
            <div
              onClick={() => setRealismLevel('realistic')}
              className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                realismLevel === 'realistic' 
                  ? 'border-blue-500 ring-4 ring-blue-500/50' 
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="aspect-[9/16] bg-gray-900 relative">
                <img 
                  src="https://hotbump.online/static/realistic_example.jpg" 
                  alt="Realistic"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-500">Realistic<br/>Example</div>';
                  }}
                />
              </div>
              <div className="p-4 bg-gray-900">
                <h3 className="text-xl font-bold text-white mb-2 text-center">
                  {language === 'en' ? 'Realistic' : 'Просто реалистично'}
                </h3>
                <p className="text-sm text-gray-400 text-center">
                  {language === 'en' 
                    ? 'Stylized, artistic images' 
                    : 'Стилизованные, художественные изображения'}
                </p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Step 1: Appearance */}
        {step === 1 && (
        <div
          className="bg-gray-800 rounded-lg p-6 min-h-[600px]"
          style={{ width: '864px', maxWidth: '864px' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">{t.step1}</h2>
            <button
              type="button"
              onClick={handleRandomAppearance}
              className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-white text-sm font-medium transition"
            >
              {t.randomAppearance}
            </button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.ethnicity}
              </label>
              <select
                value={appearance.ethnicity}
                onChange={(e) => setAppearance({ ...appearance, ethnicity: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              >
                <option value="">{t.select}</option>
                {ethnicityOptions.map(opt => (
                  <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.skinTone}
              </label>
              <select
                value={appearance.skin_tone}
                onChange={(e) => setAppearance({ ...appearance, skin_tone: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              >
                <option value="">{t.select}</option>
                {skinToneOptions.map(opt => (
                  <option key={opt} value={opt.toLowerCase().replace(' ', '_')}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.eyeColor}
              </label>
              <select
                value={appearance.eye_color}
                onChange={(e) => setAppearance({ ...appearance, eye_color: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              >
                <option value="">{t.select}</option>
                {eyeColorOptions.map(opt => (
                  <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.hairColor}
              </label>
              <select
                value={appearance.hair_color}
                onChange={(e) => setAppearance({ ...appearance, hair_color: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              >
                <option value="">{t.select}</option>
                {hairColorOptions.map(opt => (
                  <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.hairStyle}
              </label>
              <select
                value={appearance.hair_style}
                onChange={(e) => setAppearance({ ...appearance, hair_style: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              >
                <option value="">{t.select}</option>
                {hairStyleOptions.map(opt => (
                  <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.bodyType}
              </label>
              <select
                value={appearance.body_type}
                onChange={(e) => setAppearance({ ...appearance, body_type: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              >
                <option value="">{t.select}</option>
                {bodyTypeOptions.map(opt => (
                  <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {characterGender === 'male' ? t.breastSizeMale : t.breastSize}
              </label>
              <select
                value={appearance.breast_size}
                onChange={(e) => setAppearance({ ...appearance, breast_size: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              >
                <option value="">{t.select}</option>
                {breastSizeOptions.map(opt => (
                  <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.buttSize}
              </label>
              <select
                value={appearance.butt_size}
                onChange={(e) => setAppearance({ ...appearance, butt_size: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              >
                <option value="">{t.select}</option>
                {buttSizeOptions.map(opt => (
                  <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                ))}
              </select>
            </div>
            </div>
          </div>
        </div>
        )}

        {/* Step 2: Character Info */}
        {step === 2 && (
        <div
          className="bg-gray-800 rounded-lg p-6 min-h-[600px]"
          style={{ width: '864px', maxWidth: '864px' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">{t.step2}</h2>
            <button
              type="button"
              onClick={handleRandomCharacterInfo}
              disabled={randomInfoLoading}
              className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-white text-sm font-medium transition"
            >
              {randomInfoLoading ? t.generatingInfo : t.randomCharacterInfo}
            </button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.characterName}
              </label>
              <input
                type="text"
                value={characterInfo.name}
                onChange={(e) => setCharacterInfo({ ...characterInfo, name: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
                placeholder={t.namePlaceholder}
                maxLength={24}
              />
              <p className="text-xs text-gray-400 mt-1">{characterInfo.name.length}/24</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.age}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={characterInfo.age}
                  onChange={(e) => setCharacterInfo({ ...characterInfo, age: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-md appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  placeholder={t.agePlaceholder}
                  min={18}
                  max={60}
                  style={{
                    MozAppearance: 'textfield'
                  }}
                />
                <div className="absolute right-2 top-0 bottom-0 flex flex-col justify-center gap-0.5 pointer-events-none">
                  <button
                    type="button"
                    onClick={() => {
                      const current = parseInt(characterInfo.age) || 18;
                      if (current < 60) {
                        setCharacterInfo({ ...characterInfo, age: String(current + 1) });
                      }
                    }}
                    className="text-gray-400 hover:text-burgundy-400 transition-colors pointer-events-auto"
                    style={{ fontSize: '10px', lineHeight: '1' }}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const current = parseInt(characterInfo.age) || 18;
                      if (current > 18) {
                        setCharacterInfo({ ...characterInfo, age: String(current - 1) });
                      }
                    }}
                    className="text-gray-400 hover:text-burgundy-400 transition-colors pointer-events-auto"
                    style={{ fontSize: '10px', lineHeight: '1' }}
                  >
                    ▼
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.greeting}
            </label>
            <textarea
              value={characterInfo.greeting}
              onChange={(e) => setCharacterInfo({ ...characterInfo, greeting: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              placeholder={t.greetingPlaceholder}
            />
            <p className="text-xs text-gray-400 mt-1">{language === 'en' ? 'This will be the first message' : 'Это будет первое сообщение'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.scenario}
            </label>
            <textarea
              value={characterInfo.scenario}
              onChange={(e) => setCharacterInfo({ ...characterInfo, scenario: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              placeholder={t.scenarioPlaceholder}
            />
            <p className="text-xs text-gray-400 mt-1">{language === 'en' ? 'Used for AI system prompt' : 'Используется для системного промта AI'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.publicDescription}
            </label>
            <textarea
              value={characterInfo.public_description}
              onChange={(e) => setCharacterInfo({ ...characterInfo, public_description: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              placeholder={t.descriptionPlaceholder}
            />
            <p className="text-xs text-gray-400 mt-1">{language === 'en' ? 'This will appear in ABOUT section' : 'Это будет отображаться в разделе ABOUT'}</p>
          </div>
          </div>
        </div>
        )}

        {/* Step 3: Generation */}
        {step === 3 && (
        <div
          className="bg-gray-800 rounded-lg p-6 min-h-[600px]"
          style={{ width: '864px', maxWidth: '864px' }}
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">{t.step3}</h2>
          <div className="space-y-4">
            {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.tags}
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => handleTagsChange(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              placeholder={t.tagsHint}
            />
            <p className="text-xs text-gray-400 mt-1">
              {language === 'en'
                ? `Tags used: ${tags.length}/5`
                : `Тегов: ${tags.length}/5`}
            </p>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 text-xs rounded-full bg-gray-700 text-gray-100 border border-white/10"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">
                {t.individualCharacteristics}
              </label>
              <button
                type="button"
                onClick={handleAiGenerationPrompt}
                disabled={aiPromptLoading}
                className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-white text-sm font-medium transition"
              >
                {aiPromptLoading ? (language === 'en' ? 'Generating...' : 'Генерация...') : t.aiGeneration}
              </button>
            </div>
            <textarea
              value={generationPrompt}
              onChange={(e) => setGenerationPrompt(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              placeholder={t.generationPlaceholder}
            />
            <p className="text-xs text-gray-400 mt-1">{t.generationHint}</p>
          </div>

          {avatarUrl && (
            <div className="mt-4">
              <p className="text-sm text-gray-300 mb-2">{language === 'en' ? 'Preview:' : 'Превью:'}</p>
              <img src={avatarUrl} alt="Preview" className="max-w-md rounded-lg" />
            </div>
          )}

          <div className="mt-6 p-4 bg-gray-700 rounded-lg">
            <button
              onClick={handleGenerate}
              disabled={generating || !generationPrompt.trim() || user.token_balance < 19}
              className="w-full px-6 py-3 bg-burgundy-600 hover:bg-burgundy-500 disabled:opacity-50 text-white font-semibold rounded-lg transition"
            >
              {generating ? t.generating : (characterGender === 'male' ? t.generateMale : t.generate)}
            </button>
            <p className="text-sm text-gray-400 mt-2 text-center">
              {language === 'en' ? 'Cost: 19 tokens' : 'Стоимость: 19 токенов'}
            </p>
          </div>

          <div className="mt-6">
            <button
              onClick={handleCreate}
              disabled={loading || !avatarUrl || user.token_balance < 39}
              className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded-lg transition"
            >
              {loading ? (language === 'en' ? 'Creating...' : 'Создание...') : t.create}
            </button>
            <p className="text-sm text-gray-400 mt-2 text-center">
              {language === 'en' ? 'Cost: 39 tokens' : 'Стоимость: 39 токенов'}
            </p>
            <p className="text-xs text-gray-500 mt-3 text-center">{t.moderationNote}</p>
          </div>
          </div>
        </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 mx-auto" style={{ width: '864px', maxWidth: '864px' }}>
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50"
        >
          {t.back}
        </button>

        {step < 3 && (
          <button
            onClick={handleNext}
            disabled={step === 0 && !realismLevel}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg"
          >
            {t.next}
          </button>
        )}
      </div>
    </div>
  );
}

export default CreateCharacter;
